import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { BaseCellComponent } from '../base-cell.component';
import { ViewManager } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { FileManagementService } from 'src/app/services/file-management.service';
import { Subject, takeUntil, debounceTime, switchMap, EMPTY } from 'rxjs';

@Component({
  selector: 'bhub-image-cell',
  standalone: false,
  templateUrl: './image-cell.component.html',
  styleUrl: './image-cell.component.scss'
})
export class ImageCellComponent extends BaseCellComponent implements OnInit, OnDestroy {
  @Input() viewManager: ViewManager | null = null;

  // UI Properties
  imageSource: 'static' | 'context' = 'static';
  imagePath: string = '';
  safeImageUrl: SafeUrl | null = null;
  imageWidth: number = 0; // 0 means auto/fit to cell
  imageHeight: number = 0; // 0 means auto/fit to cell
  objectFit: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down' = 'contain';
  altText: string = 'Image';
  imageLoadError: boolean = false;
  isLoadingImage: boolean = false;

  // Configuration properties
  contextObjectKey: string = '';
  fieldName: string = '';

  private destroy$ = new Subject<void>();

  // Debounced image loading to prevent duplicate requests
  private loadImage$ = new Subject<string>();
  private currentImagePath: string = '';

  constructor(
    private sanitizer: DomSanitizer,
    private fileService: FileManagementService
  ) {
    super();

    // Set up debounced image loading to prevent duplicate requests
    this.loadImage$
      .pipe(
        debounceTime(100), // Wait 100ms between requests
        switchMap((filePath: string) => {
          if (filePath === this.currentImagePath && this.safeImageUrl) {
            // Don't reload if it's the same image and already loaded
            return EMPTY;
          }
          this.currentImagePath = filePath;

          if (!filePath) {
            this.setImageUrl('');
            return EMPTY;
          }

          // Determine storage location from file path
          const fromAdmin = filePath.startsWith('admin/') || filePath.startsWith('_admin/');
          const fromOrganization = filePath.startsWith('organization/');

          this.isLoadingImage = true;
          return this.fileService.getSignedUrl(filePath, fromAdmin, fromOrganization, 60);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response) => {
          if (response && response.url) {
            // Check if it's a local file URL (development environment)
            if (response.url.startsWith('file://')) {
              // For local development, use the download endpoint to get blob
              const fromAdmin = this.currentImagePath.startsWith('admin/') || this.currentImagePath.startsWith('_admin/');
              this.loadImageAsBlob(this.currentImagePath, fromAdmin);
            } else {
              // Production: use the signed URL directly
              this.isLoadingImage = false;
              this.setImageUrl(response.url);
            }
          } else if (response) {
            this.isLoadingImage = false;
            this.imageLoadError = true;
            console.error('No URL returned for file:', this.currentImagePath);
          }
        },
        error: (error) => {
          this.isLoadingImage = false;
          this.imageLoadError = true;
          console.error('Error fetching signed URL for image:', this.currentImagePath, error);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected initializeCell(): void {
    this.updateImageConfiguration();
    this.loadImage();
  }

  protected onCellOrDataChange(): void {
    this.updateImageConfiguration();
    this.loadImage();
  }

  protected onCellUpdate(): void {
    this.updateImageConfiguration();
    this.loadImage();
  }

  protected onDataSet(data: any): void {
    this.loadImage();
  }

  reloadData(): void {
    // Override to refresh image when context data changes
    this.updateImageConfiguration();
    this.loadImage();
  }

  private updateImageConfiguration(): void {
    if (!this.cell) return;

    // Get configuration
    this.imageSource = this.getConfigValue('imageSource', 'static') as 'static' | 'context';
    this.imageWidth = this.getConfigValue('imageWidth', 0);
    this.imageHeight = this.getConfigValue('imageHeight', 0);
    this.objectFit = this.getConfigValue('objectFit', 'contain') as 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
    this.altText = this.getConfigValue('altText', 'Image');

    // Get data binding configuration
    this.contextObjectKey = this.getDataBindingValue('dataObjectKey') || '';
    this.fieldName = this.getDataBindingValue('fieldName') || '';
  }

  private loadImage(): void {
    this.imageLoadError = false;

    let filePath = '';

    if (this.imageSource === 'static') {
      // Load from static file path
      filePath = this.getConfigValue('staticFilePath', '');
    } else if (this.imageSource === 'context' && this.contextObjectKey && this.fieldName && this.viewManager) {
      // Load from context data
      const dataObject = this.viewManager.contextData[this.contextObjectKey];
      if (dataObject) {
        if (Array.isArray(dataObject) && dataObject.length > 0) {
          filePath = dataObject[0][this.fieldName] || '';
        } else if (typeof dataObject === 'object' && dataObject !== null) {
          filePath = dataObject[this.fieldName] || '';
        }
      }
    }

    // Store the path for display
    this.imagePath = filePath;

    // Emit to the debounced Subject to load the image
    this.loadImage$.next(filePath);
  }

  private loadImageAsBlob(filePath: string, fromAdmin: boolean): void {
    this.fileService.downloadFile(filePath, fromAdmin)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          this.isLoadingImage = false;
          // Create object URL from the blob
          const objectUrl = window.URL.createObjectURL(blob);
          this.setImageUrl(objectUrl);
        },
        error: (error) => {
          this.isLoadingImage = false;
          this.imageLoadError = true;
          console.error('Error loading image as blob:', filePath, error);
        }
      });
  }

  private setImageUrl(url: string): void {
    if (url) {
      // Sanitize the URL for security
      this.safeImageUrl = this.sanitizer.bypassSecurityTrustUrl(url);
    } else {
      this.safeImageUrl = null;
    }
  }

  onImageError(): void {
    this.imageLoadError = true;
    console.error('Failed to load image:', this.imagePath);
  }

  onImageLoad(): void {
    this.imageLoadError = false;
  }

  get imageStyles(): any {
    const styles: any = {
      'object-fit': this.objectFit
    };

    if (this.imageWidth > 0) {
      styles['width'] = `${this.imageWidth}px`;
    } else {
      styles['width'] = '100%';
    }

    if (this.imageHeight > 0) {
      styles['height'] = `${this.imageHeight}px`;
    } else {
      styles['height'] = '100%';
    }

    return styles;
  }
}
